/**
 * Produces the FullCalendar `EventInput[]` for the blue "Available"
 * overlay on the manual calendar. Combines:
 *
 *   common availability (this week)
 *   − busy events on interviewers' calendars
 *   − minimum-notice window
 *   = bookable slots
 *
 * Returns the rendered event inputs plus a raw `AvailableSlot[]` in
 * case callers want the data (e.g. "no slots" empty-state logic).
 */
import { useMemo } from 'react';
import type { EventInput } from '@fullcalendar/core';
import {
  applyMinimumNotice,
  subtractBusyFromAvailability,
  type AvailableSlot,
  type BusyEvent,
  type Interval,
} from '../../availability';
import { EVENT_PRIORITY, EVENT_STYLES } from '../events/eventStyles';
import {
  DEFAULT_MIN_SLOT_MINUTES,
  EVENT_CATEGORIES,
  EVENT_CLASS_NAMES,
} from '../constants';

export interface UseAvailableSlotsArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  timezone: string;
  minSlotMinutes?: number;
  minimumNoticeHours?: number;
}

export interface UseAvailableSlotsResult {
  slots: AvailableSlot[];
  events: EventInput[];
}

export function useAvailableSlots({
  availability,
  busyEvents,
  timezone,
  minSlotMinutes = DEFAULT_MIN_SLOT_MINUTES,
  minimumNoticeHours = 0,
}: UseAvailableSlotsArgs): UseAvailableSlotsResult {
  return useMemo(() => {
    const raw = subtractBusyFromAvailability({
      availability,
      busyEvents,
      timezone,
      minSlotMinutes,
    });
    const filtered = applyMinimumNotice({
      slots: raw,
      noticeHours: minimumNoticeHours,
      timezone,
      minSlotMinutes,
    });

    const events: EventInput[] = filtered.map((slot) => ({
      id: slot.id,
      start: slot.start,
      end: slot.end,
      title: 'Available',
      // Styling lives in calendar.css `.im-available-slot` — transparent
      // body + animated primary-100 underlay. Leaving these unset keeps
      // FullCalendar from painting over the class.
      backgroundColor: 'transparent',
      borderColor: EVENT_STYLES.available.borderColor,
      textColor: EVENT_STYLES.available.textColor,
      editable: false,
      classNames: EVENT_CLASS_NAMES.available,
      extendedProps: {
        category: EVENT_CATEGORIES.available,
        priority: EVENT_PRIORITY.available,
      },
    }));

    return { slots: filtered, events };
  }, [availability, busyEvents, timezone, minSlotMinutes, minimumNoticeHours]);
}
